import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import TopNav from '../components/sections/TopNav';
import SiteFooter from '../components/sections/SiteFooter';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/** Formats a countdown from seconds → "Hh MMm SSs" so the scarcity feels
 *  tangible. Falls back to "0h 00m 00s" when the window has closed - the
 *  parent hides the whole banner in that case. */
const fmt = (secs) => {
  if (!secs || secs <= 0) return '0h 00m 00s';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
};

const Login = () => {
  const { requestMagicLink, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');
  // Resend cooldown: the button unlocks 60s after the last send.
  const [cooldown, setCooldown] = useState(0);
  const [resendNote, setResendNote] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);
  // Surface any error handed back by the Google callback (e.g. banned email).
  useEffect(() => {
    const err = params.get('err');
    if (err) setError(err);
  }, [params]);
  // Live countdown for the 24h open-access grace window. Fetches once on
  // mount (never re-hits the API - we tick locally) and hides itself the
  // instant the window closes so the banner never shows a stale promise.
  const [secondsLeft, setSecondsLeft] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    fetch(`${API}/api/me?client_id=login-grace-check`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const g = data.grace || {};
        if (g.active && (g.seconds_remaining ?? 0) > 0) {
          setSecondsLeft(g.seconds_remaining);
          timer = setInterval(() => {
            setSecondsLeft((s) => (s === null || s <= 1 ? 0 : s - 1));
          }, 1000);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, []);

  if (user) {
    // Already signed in: bounce them straight to /subscribe if they don't
    // have access yet, else /account. This unifies the flow whether they
    // arrive at /login by direct URL or by clicking a plan on /subscribe.
    setTimeout(() => navigate(user.plan && user.plan !== 'free' ? '/account' : '/subscribe'), 0);
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      await requestMagicLink(email.trim());
      setStatus('sent');
      setCooldown(60);
    } catch (err) {
      setError(err.message || 'Could not send login link.');
      setStatus('error');
    }
  };

  const onResend = async () => {
    if (cooldown > 0 || resending) return;
    setResendNote('');
    setError('');
    setResending(true);
    try {
      await requestMagicLink(email.trim());
      setResendNote('Sent again. Check your inbox and your spam folder.');
      setCooldown(60);
    } catch (err) {
      setError(err.message || 'Could not send login link.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="login-page">
      <TopNav />
      <main className="max-w-md mx-auto px-4 py-14">
        <p className="text-red-500 text-[11px] font-black uppercase tracking-[0.3em] mb-2">Outlaw Media</p>
        <h1 className="text-3xl sm:text-4xl font-black mb-4" style={{ fontFamily: 'Georgia, serif' }}>Log in or sign up</h1>

        {/* Signup + paid tiers so mobile visitors see the site is
            subscribers-only. Header uses an emerald accent to break
            visually from the red paid tiers. */}
        <div className="mb-6 rounded-lg border border-zinc-800 bg-zinc-950/70 overflow-hidden" data-testid="signup-pricing-block">
          <div className="bg-emerald-500/10 border-b border-emerald-800/60 px-4 py-3">
            <p className="text-emerald-400 text-[10px] font-black uppercase tracking-[0.3em] mb-1">Sign up</p>
            <p className="text-white text-sm leading-snug">
              <span className="font-black">Subscribers only.</span> Sign up in one tap · no password. Pick your plan next.
            </p>
          </div>
          <div className="bg-red-600/10 border-b border-red-900/50 px-4 py-2">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-400">
              Choose your unlimited plan
            </p>
          </div>
          <ul className="text-sm text-zinc-300 divide-y divide-zinc-900">
            <li className="flex justify-between items-center gap-3 px-4 py-2.5">
              <span><strong className="text-white">Monthly</strong> - unlimited everything, cancel any time</span>
              <span className="text-red-400 text-[10px] font-black uppercase tracking-widest whitespace-nowrap">$13 / mo</span>
            </li>
            <li className="flex justify-between items-center gap-3 px-4 py-2.5">
              <span><strong className="text-white">Annual</strong> - $26 off vs monthly</span>
              <span className="text-red-400 text-[10px] font-black uppercase tracking-widest whitespace-nowrap">$130 / yr</span>
            </li>
            <li className="flex justify-between items-center gap-3 px-4 py-2.5">
              <span><strong className="text-white">Founding</strong> - lifetime access, name on Founders page (80 places)</span>
              <span className="text-yellow-400 text-[10px] font-black uppercase tracking-widest whitespace-nowrap">$249 once</span>
            </li>
          </ul>
          <p className="px-4 py-2.5 text-[11px] text-zinc-500 bg-zinc-950 border-t border-zinc-900">
            All prices AUD. Cancel any time.
          </p>
          <p
            className="px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.25em] text-red-400 bg-red-950/30 border-t border-red-900/60"
            data-testid="login-sharing-policy"
          >
            One account per person · Any account sharing = ban without refund
          </p>
        </div>

        {status === 'sent' ? (
          <div data-testid="login-sent" className="rounded border border-emerald-700/70 bg-emerald-950/40 px-4 py-4 text-sm">
            <p className="font-bold mb-1">Check your inbox.</p>
            <p className="text-zinc-300">We&apos;ve sent a one-tap login link to <strong>{email}</strong>. It expires in 15 minutes.</p>
            <p className="text-zinc-500 text-xs mt-2">Nothing after a minute? Check spam, then send it again.</p>
            {resendNote && <p data-testid="login-resend-note" className="text-emerald-400 text-xs mt-2">{resendNote}</p>}
            {error && <p data-testid="login-resend-error" className="text-red-400 text-xs mt-2">{error}</p>}
            <button
              type="button"
              data-testid="login-resend"
              onClick={onResend}
              disabled={cooldown > 0 || resending}
              className="mt-3 w-full border border-emerald-700/70 hover:bg-emerald-900/40 disabled:opacity-50 disabled:hover:bg-transparent text-white font-black text-[11px] uppercase tracking-[0.3em] py-3 rounded transition-colors"
            >
              {resending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend login link'}
            </button>
            <button
              type="button"
              data-testid="login-change-email"
              onClick={() => { setStatus('idle'); setResendNote(''); setError(''); }}
              className="mt-2 w-full text-zinc-400 hover:text-white text-[11px] uppercase tracking-[0.25em] py-2 transition-colors"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <>
            {/* Google sign-in - Emergent-managed OAuth. Redirects to
                auth.emergentagent.com; on return, AuthGoogleCallback swaps
                the session_id for our cookie.
                REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
                REDIRECT URLS, THIS BREAKS THE AUTH - always derive from
                window.location.origin. */}
            <button
              type="button"
              data-testid="login-google"
              onClick={() => {
                // After Google returns, land on /subscribe so newly-signed-in
                // readers finish picking a plan in the same flow. Existing
                // subscribers see the "already on X access · Manage billing"
                // banner at the top of that page.
                const redirectUrl = window.location.origin + '/subscribe';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full flex items-center justify-center gap-3 bg-white hover:bg-zinc-100 text-black font-black text-xs uppercase tracking-[0.3em] py-3 rounded transition-colors mb-4"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-3 my-4" aria-hidden>
              <div className="flex-1 h-px bg-zinc-800" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">or email link</span>
              <div className="flex-1 h-px bg-zinc-800" />
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-2">Email</span>
              <input
                type="email"
                required
                data-testid="login-email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-3 text-white focus:outline-none focus:border-red-500"
                placeholder="you@example.com"
              />
            </label>
            {error && <p data-testid="login-error" className="text-red-400 text-sm">{error}</p>}
            <button
              type="submit"
              data-testid="login-submit"
              disabled={status === 'sending'}
              className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-black text-xs uppercase tracking-[0.3em] py-3 rounded transition-colors"
            >
              {status === 'sending' ? 'Sending…' : 'Send login link'}
            </button>
            <p className="text-zinc-500 text-xs">
              No account yet? Same form - we&apos;ll create one and email you the login link.
            </p>
            <p className="text-zinc-500 text-xs">
              Ready to go unlimited? <Link to="/subscribe" className="text-red-400 underline">See paid plans</Link>.
            </p>
          </form>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default Login;
